import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon, SectionLabel, MobileHeader, EmptyState } from '@/components';
import { useToast } from '@/components';
import { useAuth } from '@/auth/AuthProvider';
import { useCourse, useEnroll } from '@/hooks/queries';
import { useLocalized } from '@/hooks/useLocalized';

export function LearnerEnroll() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { L } = useLocalized();
  const { toast } = useToast();
  const { data: course } = useCourse(id);
  const enroll = useEnroll();
  const hasClasses = course && course.type !== 'online';
  const [done, setDone] = useState(false);

  if (!course || !user) return <EmptyState icon="book" title="Course not found" />;

  if (done) {
    return (
      <div style={{ background: 'var(--paper)', minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ padding: 32, textAlign: 'center', maxWidth: 420 }}>
          <div style={{ width: 96, height: 96, borderRadius: '50%', background: 'var(--sage-soft)', display: 'grid', placeItems: 'center', color: 'var(--sage)', margin: '0 auto', animation: 'pop .5s cubic-bezier(.2,.7,.2,1) both' }}>
            <Icon name="check" size={48} stroke={2.2} />
          </div>
          <h2 style={{ fontSize: 24, fontWeight: 600, margin: '26px 0 6px' }}>You're enrolled</h2>
          <div className="th" style={{ color: 'var(--ink-3)', fontSize: 14 }}>ลงทะเบียนสำเร็จ</div>
          <p style={{ color: 'var(--ink-2)', fontSize: 15, lineHeight: 1.55, margin: '18px auto 0', maxWidth: 320 }}>
            <strong>{course.title.en}</strong>. We've added it to your learning.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 32, maxWidth: 320, marginInline: 'auto' }}>
            <button className="btn btn-primary btn-lg btn-block" onClick={() => navigate(`/learn/catalog/${id}`)}>Go to the course <Icon name="arrowR" size={17} /></button>
            <button className="btn btn-quiet btn-block" onClick={() => navigate('/learn/learning')}>My learning</button>
          </div>
        </div>
      </div>
    );
  }

  const confirm = () => {
    enroll.mutate(id, {
      onSuccess: () => {
        toast('Enrolled', { icon: 'check' });
        setDone(true);
      },
    });
  };

  const details: [string, string][] = [
    ['Name', `${user.firstEn} ${user.lastEn}`],
    ['Unit', `${user.deptId} · ${user.unitId}`],
  ];

  return (
    <div style={{ background: 'var(--paper)', minHeight: '100%' }}>
      <MobileHeader title="Enroll" th="ลงทะเบียน" onBack={() => navigate(-1)} large={false} />
      <div className="hidden md:block" style={{ padding: '18px 28px 0' }}>
        <button onClick={() => navigate(-1)} className="btn btn-ghost btn-sm"><Icon name="chevL" size={15} /> Back</button>
      </div>
      <div style={{ maxWidth: 560, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 22, padding: '18px 20px 110px' }}>
        <div className="card" style={{ padding: 16 }}>
          <div className="th" style={{ fontSize: 16, fontWeight: 600 }}>{L(course.title)}</div>
          <div style={{ fontSize: 13.5, color: 'var(--ink-3)', marginTop: 2 }}>{course.title.en} · {course.host}</div>
        </div>

        {hasClasses ? (
          <div className="card" style={{ padding: 16, background: 'var(--clay-soft)', borderColor: 'transparent' }}>
            <div style={{ display: 'flex', gap: 12 }}>
              <Icon name="pin" size={20} style={{ color: 'var(--clay)', flex: 'none' }} />
              <div>
                <div style={{ fontSize: 14.5, fontWeight: 600 }}>Onsite session · ภาคปฏิบัติ</div>
                <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 3 }}>{course.seminar ?? 'Schedule to be announced'}{course.venue ? ` · ${course.venue}` : ''}</div>
                <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 4 }}>You'll check in with the QR code on the day.</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="card" style={{ padding: 16 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <Icon name="globe" size={20} style={{ color: 'var(--slate)' }} />
              <div>
                <div style={{ fontSize: 14.5, fontWeight: 600 }}>Self-paced online</div>
                <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Start immediately, finish by {course.enrollEnd}.</div>
              </div>
            </div>
          </div>
        )}

        <div>
          <SectionLabel>Confirm your details</SectionLabel>
          <div className="card divide">
            {details.map(([l, v], i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px' }}>
                <span style={{ fontSize: 13.5, color: 'var(--ink-3)' }}>{l}</span>
                <span style={{ fontSize: 14, fontWeight: 500 }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ position: 'sticky', bottom: 0, padding: '14px 20px 28px', background: 'linear-gradient(180deg, transparent, var(--paper) 28%)' }}>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <button className="btn btn-primary btn-lg btn-block" disabled={enroll.isPending} onClick={confirm}>
            {enroll.isPending ? 'Enrolling…' : 'Confirm enrollment'}
          </button>
        </div>
      </div>
    </div>
  );
}
